import { Box, IconButton, SvgIcon, Tooltip, Typography } from '@mui/material';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@Components/Button';
import Icons from '@Constants/Icons.constants';
import EmailFileUpload from '@Containers/Emails/EmailFileUpload/EmailFileUpload.container';
import { FileUploadResponse } from '@Models/upload.models';
import { setUploadData } from '@Slices/upload.slice';
import { AttachmentType } from '@Typings/emailUpload.types';
import { Icon } from '@Typings/global.types';
import { useAppDispatch, useAppSelector } from '@Utils/hooks/useStore.hook';

import { EmailFileAttachmentProps } from './EmailFileAttachment.types';

const EmailFileAttachment: FunctionComponent<EmailFileAttachmentProps> = (
  props: EmailFileAttachmentProps
): JSX.Element => {
  const {
    viewOnly,
    attachments,
    emailPreviewBtnProps,
    isPreview,
    ...resProps
  } = props;

  /** REDUX STATE */
  const uploadState = useAppSelector((state) => state.emailUpload);
  const { data, isLoading } = uploadState.listOfAttachments;

  /** HOOKS */
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  /** Tooltip message */
  const tooltip = t('common.tooltip.Download is not available');

  /** Handles deletion of uploaded file from store as well as server */
  const onDelete = (index: number): void => {
    const newAttachment = [...data];
    newAttachment?.splice(index, 1);
    dispatch(
      setUploadData({
        storeKey: AttachmentType.Attachment,
        attachment: {
          isLoading,
          data: newAttachment,
        },
      })
    );
  };

  return (
    <Box>
      {attachments?.map((value: FileUploadResponse, index: number) => {
        let fileIcon: Icon;
        /** Setting fileIcon according to extension in file name */
        const extension = value.filename?.split('.').at(-1);
        switch (extension) {
          case 'csv':
            fileIcon = 'csv';
            break;
          case 'doc':
          case 'docx':
            fileIcon = 'doc';
            break;
          case 'pdf':
            fileIcon = 'pdf';
            break;
          default:
            fileIcon = 'file';
            break;
        }

        return (
          <Tooltip title={tooltip} placement="top-start" key={index}>
            <Box
              sx={{
                px: 0.8,
                mb: 0.5,
                minHeight: '5.6rem',
                backgroundColor: ({ colors }) => colors.greyBackground,
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box display="flex" alignItems="center">
                <SvgIcon
                  sx={{
                    marginRight: 0.8,
                    color: ({ colors }) => colors.iconGrey,
                  }}
                >
                  {Icons[fileIcon]}
                </SvgIcon>
                <Box>
                  <Typography variant="input" sx={{ color: 'text.secondary' }}>
                    {value.filename}
                  </Typography>
                  <Typography
                    variant="helpText"
                    sx={{ color: 'text.secondary' }}
                  >{`${value.bytes / 1000} KB`}</Typography>
                </Box>
              </Box>
              {!viewOnly && (
                <IconButton onClick={() => onDelete(index)}>
                  <SvgIcon>{Icons.close}</SvgIcon>
                </IconButton>
              )}
            </Box>
          </Tooltip>
        );
      })}
      {!isPreview && (
        <Box display="flex">
          {attachments && attachments.length < 2 && !viewOnly && (
            <EmailFileUpload
              storeKey={AttachmentType.Attachment}
              uploadBtnIconProps={{ sx: { transform: 'rotate(270deg)' } }}
              {...resProps}
            />
          )}
          <Button
            variant="text"
            icon="visibility"
            color="secondary"
            sx={{ textTransform: 'none', ml: 'auto' }}
            {...emailPreviewBtnProps}
          >
            {t('Email.Preview Email')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EmailFileAttachment;
